import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCustomerById,
  updateCustomer,
} from "../../../services/clearing/customers";

import { Toast } from "primereact/toast";

import CustomerForm from "../components/forms/CustomerForm";
import { validateCustomerForm } from "../utils/customerValidation";

const CustomerEdit = () => {
  const params = useParams<{ id: string }>();
  const customerId = params?.id || null;
  const navigate = useNavigate();
  const toast = useRef(null);

  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    if (customerId) {
      getCustomerById(customerId)
        .then((loaded) =>
          setCustomer({
            // Legacy customers may not include this field; default it so the
            // update payload always carries a value (omitting it resets to false).
            attach_csv_to_credit_note: false,
            ...loaded,
          })
        )
        .catch(() =>
          toast?.current.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to load customer",
            life: 3000,
          })
        );
    }
  }, [customerId]);

  const handleChange = (field: string, value: any, nested = false) => {
    if (nested) {
      // supports nested property accessors, e.g. address.street or address.recipient.name
      setCustomer((prev) => {
        const newCustomer = { ...prev };
        const keys = field.split(".");
        let current = newCustomer;

        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== "object") {
            current[key] = {}; // create object if missing
          }
          current = current[key];
        }

        current[keys[keys.length - 1]] = value;

        return newCustomer;
      });
    } else {
      setCustomer((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async () => {
    const missingFields = validateCustomerForm(customer);

    if (missingFields.length > 0) {
      toast?.current.show({
        severity: "error",
        summary: "Missing Required Fields",
        detail: `Please fill in the following fields: ${missingFields.join(
          ", "
        )}`,
        life: 5000,
      });
      return;
    }

    try {
      const updated = await updateCustomer(customer);
      if (updated) {
        setCustomer(updated);
      }
      toast?.current.show({
        severity: "success",
        summary: "Success",
        detail: "Customer updated successfully",
        life: 3000,
      });
      setTimeout(() => {
        navigate("/customers");
      }, 1500);
    } catch {
      toast?.current.show({
        severity: "error",
        summary: "Error",
        detail: "Update failed",
        life: 3000,
      });
    }
  };

  if (!customer) return <p className="p-4">Loading...</p>;

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <CustomerForm
        title="Customer Details"
        submitLabel="Update Customer"
        customer={customer}
        onFieldChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate("/customers")}
      />
    </div>
  );
};

export default CustomerEdit;
